/* crm-notifications.jsx — Notification system components for Phase 4
   Provides: NotificationItem, NotificationCenter, NotificationBell,
             NotificationToast, NotificationPreferences
   Self-registers on window. */

const { useState, useEffect, useMemo, useRef, useCallback } = React;

/* ── Inject panel animation styles once ── */
if (typeof document !== 'undefined' && !document.getElementById('nx-notif-styles')) {
  const _s = document.createElement('style');
  _s.id = 'nx-notif-styles';
  _s.textContent = [
    '@keyframes nx-notif-in { from { opacity:0; transform:translateY(-6px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }',
    '.nx-notif-panel { animation: nx-notif-in 0.18s cubic-bezier(0.16,1,0.3,1) both; }',
    '.nx-notif-panel { background:var(--nx-surface); border:1px solid var(--nx-border); border-radius:12px; box-shadow:0 8px 32px rgba(0,0,0,0.14),0 2px 8px rgba(0,0,0,0.08); overflow:hidden; }',
    '.nx-notif-row { display:flex; align-items:flex-start; gap:12px; padding:12px 16px; cursor:pointer; border-bottom:1px solid var(--nx-border); transition:background 0.12s; outline:none; }',
    '.nx-notif-row:last-child { border-bottom:none; }',
    '.nx-notif-row:hover, .nx-notif-row:focus-visible { background:var(--nx-surface-alt) !important; }',
    '.nx-notif-row.is-unread { background:var(--nx-surface-tint,rgba(99,102,241,0.04)); }',
    '.nx-notif-icon { width:34px; height:34px; border-radius:50%; display:flex; align-items:center; justify-content:center; flex-shrink:0; }',
    '.nx-notif-body { flex:1; min-width:0; }',
    '.nx-notif-title { font-size:13px; font-weight:600; color:var(--nx-text); line-height:1.4; word-break:break-word; }',
    '.nx-notif-snippet { font-size:12px; color:var(--nx-text-muted); margin-top:2px; line-height:1.4; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; word-break:break-word; }',
    '.nx-notif-meta { display:flex; align-items:center; gap:6px; margin-top:4px; }',
    '.nx-notif-time { font-size:11px; color:var(--nx-text-faint,var(--nx-text-muted)); }',
    '.nx-notif-dot { width:6px; height:6px; border-radius:50%; background:var(--nx-accent); flex-shrink:0; }',
    '.nx-notif-header { display:flex; align-items:center; gap:10px; padding:14px 16px 0; flex-shrink:0; }',
    '.nx-notif-tabs { display:flex; padding:0 16px; border-bottom:1px solid var(--nx-border); flex-shrink:0; margin-top:2px; }',
    '.nx-notif-tab { padding:10px 12px; font-size:12.5px; font-weight:500; color:var(--nx-text-muted); background:transparent; border:none; border-bottom:2px solid transparent; cursor:pointer; transition:color 0.12s,border-color 0.12s; white-space:nowrap; margin-bottom:-1px; }',
    '.nx-notif-tab.is-active { font-weight:700; color:var(--nx-accent); border-bottom-color:var(--nx-accent); }',
    '.nx-notif-tab:hover:not(.is-active) { color:var(--nx-text); }',
    '.nx-notif-list { flex:1; overflow-y:auto; overscroll-behavior:contain; min-height:0; }',
    '.nx-notif-empty { padding:40px 24px; text-align:center; }',
    '.nx-notif-mark-all { font-size:12px; color:var(--nx-accent); background:transparent; border:none; cursor:pointer; padding:4px 8px; border-radius:6px; font-weight:600; flex-shrink:0; transition:background 0.12s; }',
    '.nx-notif-mark-all:hover { background:var(--nx-accent-tint-2); }',
    '.nx-notif-close { background:transparent; border:none; cursor:pointer; padding:6px; border-radius:6px; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:background 0.12s; }',
    '.nx-notif-close:hover { background:var(--nx-surface-alt); }',
    '.nx-notif-badge { position:absolute; top:-1px; right:-1px; min-width:16px; height:16px; background:#EF4444; color:#fff; border-radius:8px; font-size:10px; font-weight:700; display:flex; align-items:center; justify-content:center; padding:0 3px; line-height:1; pointer-events:none; }',
    '.nx-notif-bell { position:relative; background:transparent; border:none; cursor:pointer; padding:6px; display:flex; align-items:center; justify-content:center; border-radius:8px; transition:background 0.12s; }',
    '.nx-notif-bell:hover { background:var(--nx-surface-alt); }',
    '.nx-notif-section-title { font-size:10px; font-weight:800; text-transform:uppercase; letter-spacing:0.08em; color:var(--nx-text-muted); padding:8px 16px 4px; }',
    '.nx-notif-loading { display:flex; align-items:center; justify-content:center; padding:32px; }',
    '@keyframes nx-spin { to { transform:rotate(360deg); } }',
    '.nx-notif-spinner { width:18px; height:18px; border:2px solid var(--nx-border); border-top-color:var(--nx-accent); border-radius:50%; animation:nx-spin 0.7s linear infinite; }',
  ].join('\n');
  document.head.appendChild(_s);
}

/* ── helpers ── */
const _relTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const NOTIF_ICON = {
  DEAL_ASSIGNED:    'Target',
  TASK_ASSIGNED:    'CheckSquare',
  MEETING_REMINDER: 'Calendar',
  MENTION:          'AtSign',
  STAGE_CHANGED:    'TrendingUp',
  FOLLOWUP_DUE:     'Clock',
  ACTIVITY_REMINDER:'Bell',
  COMMENT_ADDED:    'MessageCircle',
  RECORD_ASSIGNED:  'User',
};

const NOTIF_COLOR = {
  DEAL_ASSIGNED:    '#6366f1',
  TASK_ASSIGNED:    '#0EA5E9',
  MEETING_REMINDER: '#8B5CF6',
  MENTION:          '#EC4899',
  STAGE_CHANGED:    '#10B981',
  FOLLOWUP_DUE:     '#F59E0B',
  ACTIVITY_REMINDER:'#64748B',
  COMMENT_ADDED:    '#6366f1',
  RECORD_ASSIGNED:  '#0EA5E9',
};

/* ══════════════════════════════════════════════════════════════
   NOTIFICATION ITEM
   ══════════════════════════════════════════════════════════════ */
const NotificationItem = ({ notif, onMarkRead, onNavigate, tabIndex }) => {
  const icon  = NOTIF_ICON[notif.type]  || 'Bell';
  const color = NOTIF_COLOR[notif.type] || 'var(--nx-accent)';

  const handleClick = () => {
    onMarkRead?.(notif.id);
    if (notif.recordId) onNavigate?.(notif);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); }
  };

  return (
    <button
      type="button"
      tabIndex={tabIndex ?? 0}
      onClick={handleClick}
      onKeyDown={handleKey}
      className={`nx-notif-row${notif.read ? '' : ' is-unread'}`}
      aria-label={notif.title}
    >
      <div className="nx-notif-icon" style={{ background: color + '1a' }}>
        <NexusIcon name={icon} size={14} style={{ color }} />
      </div>
      <div className="nx-notif-body">
        <div className="nx-notif-title">{notif.title || notif.type}</div>
        {notif.body && <div className="nx-notif-snippet">{notif.body}</div>}
        <div className="nx-notif-meta">
          {!notif.read && <span className="nx-notif-dot" aria-hidden="true" />}
          <span className="nx-notif-time">{_relTime(notif.createdAt || notif.timestamp)}</span>
        </div>
      </div>
    </button>
  );
};

/* ══════════════════════════════════════════════════════════════
   NOTIFICATION CENTER
   ══════════════════════════════════════════════════════════════ */
/* ── Empty state — extracted to avoid nested ternaries in JSX ── */
const _EMPTY_ICON  = { Mentions: 'AtSign', Assignments: 'CheckSquare' };
const _EMPTY_TITLE = { Unread: 'All caught up' };
const _EMPTY_BODY  = {
  Unread:      'No unread notifications.',
  Mentions:    'No one has mentioned you yet.',
  Assignments: 'No records assigned to you yet.',
};
const EmptyState = ({ activeTab }) => (
  <div className="nx-notif-empty">
    <NexusIcon
      name={_EMPTY_ICON[activeTab] || 'BellOff'}
      size={28}
      style={{ color: 'var(--nx-text-faint,var(--nx-text-muted))', marginBottom: 10 }}
    />
    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)', marginBottom: 4 }}>
      {_EMPTY_TITLE[activeTab] || 'No notifications'}
    </div>
    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
      {_EMPTY_BODY[activeTab] || 'Notifications will appear here.'}
    </div>
  </div>
);

/* Module-level helper — keeps handleNavigate under the complexity budget */
const _findRecord = (store, recordType, recordId) => {
  const id = recordId;
  if (recordType === 'companies' || recordType === 'company') {
    return (store.companies || []).find(r => r.id === id) ?? null;
  }
  if (recordType === 'people' || recordType === 'person') {
    return (store.people || []).find(r => r.id === id) ?? null;
  }
  if (recordType === 'deals' || recordType === 'deal') {
    return (store.deals || []).find(r => r.id === id) ?? null;
  }
  if (recordType === 'tasks' || recordType === 'task') {
    return (store.tasks || []).find(r => r.id === id) ?? null;
  }
  return null;
};

/* Fetch record by ID from the API when it is not in the store (e.g., loading race) */
const _fetchRecord = async (wsId, recordType, recordId) => {
  try {
    const api = window.__nx_api;
    if (recordType === 'people'    || recordType === 'person')  return await api?.people?.getOne?.(wsId, recordId);
    if (recordType === 'companies' || recordType === 'company') return await api?.company?.getOne?.(wsId, recordId);
    if (recordType === 'deals'     || recordType === 'deal')    return await api?.deal?.getOne?.(wsId, recordId);
    if (recordType === 'tasks'     || recordType === 'task')    return await api?.task?.getOne?.(wsId, recordId);
  } catch { /* record deleted or cross-workspace — let caller handle null */ }
  return null;
};

const TABS = [
  { id: 'All',         label: 'All' },
  { id: 'Unread',      label: 'Unread' },
  { id: 'Mentions',    label: 'Mentions' },
  { id: 'Assignments', label: 'Assigned' },
];

const NotificationCenter = ({ onClose }) => {
  const wsId       = window.__nx_auth?.user?.activeWorkspaceId;
  const toast      = useToast();
  const store      = useStore();
  const [activeTab, setActiveTab]   = useState('All');
  const [items,     setItems]       = useState([]);
  const [loading,   setLoading]     = useState(false);
  const timerRef   = useRef(null);
  const listRef    = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const opts = { unreadOnly: activeTab === 'Unread' };
      const data = await window.__nx_api?.notifications?.list(wsId, opts);
      let list = Array.isArray(data) ? data : (data?.items || []);
      if (activeTab === 'Mentions')    list = list.filter(n => n.type === 'MENTION');
      if (activeTab === 'Assignments') list = list.filter(n => ['DEAL_ASSIGNED','TASK_ASSIGNED','RECORD_ASSIGNED'].includes(n.type));
      setItems(list);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [wsId, activeTab]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const handleMarkRead = useCallback(async (id) => {
    try {
      await window.__nx_api?.notifications?.markRead(wsId, id);
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch { /* silent */ }
  }, [wsId]);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await window.__nx_api?.notifications?.markAllRead(wsId);
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      toast?.({ tone: 'success', icon: 'Check', title: 'All notifications marked as read' });
    } catch {
      toast?.({ tone: 'danger', icon: 'AlertCircle', title: 'Failed to mark all as read' });
    }
  }, [wsId, toast]);

  const handleNavigate = useCallback(async (notif) => {
    if (!notif.recordId || !notif.recordType) return;
    const isDiscussionNotif = notif.type === 'COMMENT_ADDED' || notif.type === 'MENTION';
    window.__nx_discuss_intent = isDiscussionNotif ? { commentId: notif.metadata?.commentId } : null;
    let record = _findRecord(store, notif.recordType, notif.recordId);
    if (!record) {
      /* Store may not yet contain this record (loading race or cross-session navigation).
         Fall back to a direct API fetch so the drawer can open with fresh data. */
      record = await _fetchRecord(wsId, notif.recordType, notif.recordId);
    }
    if (record) {
      store.openDrawer(record);
      onClose?.();
    } else {
      window.__nx_discuss_intent = null;
    }
  }, [store, wsId, onClose]);

  const unreadCount = useMemo(() => items.filter(n => !n.read).length, [items]);

  /* Group into "Today" / "Earlier" sections */
  const todayStart = useMemo(() => {
    const d = new Date(); d.setHours(0,0,0,0); return d;
  }, []);

  const { today, earlier } = useMemo(() => {
    const t = [], e = [];
    items.forEach(n => {
      const ts = n.createdAt || n.timestamp;
      (ts && new Date(ts) >= todayStart ? t : e).push(n);
    });
    return { today: t, earlier: e };
  }, [items, todayStart]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: 'inherit', overflow: 'hidden' }}>

      {/* ── Sticky header ── */}
      <div className="nx-notif-header" style={{ borderBottom: '1px solid var(--nx-border)', paddingBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <NexusIcon name="Bell" size={15} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--nx-text)' }}>Notifications</span>
          {unreadCount > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--nx-accent)', color: '#fff', borderRadius: 10,
              fontSize: 10, fontWeight: 700, padding: '1px 6px', lineHeight: 1.5,
            }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </div>
        {unreadCount > 0 && (
          <button type="button" className="nx-notif-mark-all" onClick={handleMarkAllRead} title="Mark all as read">
            Mark all read
          </button>
        )}
        <button
          type="button"
          className="nx-notif-close"
          onClick={onClose}
          aria-label="Close notifications"
        >
          <NexusIcon name="X" size={15} style={{ color: 'var(--nx-text-muted)' }} />
        </button>
      </div>

      {/* ── Sticky tabs ── */}
      <div className="nx-notif-tabs" role="tablist" aria-label="Notification filters">
        {TABS.map(t => (
          <button
            type="button"
            key={t.id}
            role="tab"
            aria-selected={activeTab === t.id}
            className={`nx-notif-tab${activeTab === t.id ? ' is-active' : ''}`}
            onClick={() => { setActiveTab(t.id); listRef.current?.scrollTo(0, 0); }}
          >
            {t.label}
            {t.id === 'Unread' && unreadCount > 0 && (
              <span style={{
                marginLeft: 5, background: 'var(--nx-accent)', color: '#fff',
                borderRadius: 8, fontSize: 10, fontWeight: 700, padding: '0 5px', lineHeight: '16px',
                display: 'inline-block', verticalAlign: 'middle',
              }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Scrollable list ── */}
      <div ref={listRef} className="nx-notif-list" role="tabpanel">
        {loading && <div className="nx-notif-loading"><div className="nx-notif-spinner" /></div>}
        {!loading && items.length === 0 && <EmptyState activeTab={activeTab} />}
        {!loading && items.length > 0 && (
          <>
            {today.length > 0 && (
              <>
                <div className="nx-notif-section-title">Today</div>
                {today.map((n, i) => (
                  <NotificationItem key={n.id} notif={n}
                    onMarkRead={handleMarkRead} onNavigate={handleNavigate}
                    tabIndex={i === 0 ? 0 : -1} />
                ))}
              </>
            )}
            {earlier.length > 0 && (
              <>
                {today.length > 0 && <div className="nx-notif-section-title" style={{ marginTop: 4 }}>Earlier</div>}
                {earlier.map((n, i) => (
                  <NotificationItem key={n.id} notif={n}
                    onMarkRead={handleMarkRead} onNavigate={handleNavigate}
                    tabIndex={today.length === 0 && i === 0 ? 0 : -1} />
                ))}
              </>
            )}
          </>
        )}
      </div>


      {/* ── Footer ── */}
      {items.length > 0 && (
        <div style={{
          padding: '8px 16px', borderTop: '1px solid var(--nx-border)', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
            {items.length} notification{items.length !== 1 ? 's' : ''}
            {unreadCount > 0 ? ` · ${unreadCount} unread` : ' · all read'}
          </span>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   NOTIFICATION BELL
   ══════════════════════════════════════════════════════════════ */
const NotificationBell = ({ onClick }) => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const res = await window.__nx_api?.notifications?.getCount(wsId);
      setCount(typeof res === 'number' ? res : (res?.count || 0));
    } catch {
      setCount(0);
    }
  }, [wsId]);

  useEffect(() => {
    loadCount();
    timerRef.current = setInterval(loadCount, 30000);
    return () => clearInterval(timerRef.current);
  }, [loadCount]);

  return (
    <button
      type="button"
      className="nx-notif-bell"
      onClick={onClick}
      aria-label={count > 0 ? `${count} unread notifications` : 'Notifications'}
      title="Notifications"
    >
      <NexusIcon name="Bell" size={18} style={{ color: 'var(--nx-text-muted)' }} />
      {count > 0 && (
        <span className="nx-notif-badge" aria-hidden="true">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
};

/* ══════════════════════════════════════════════════════════════
   NOTIFICATION TOAST
   ══════════════════════════════════════════════════════════════ */
const NotificationToast = ({ notif, onDismiss }) => {
  const icon  = NOTIF_ICON[notif?.type]  || 'Bell';
  const color = NOTIF_COLOR[notif?.type] || 'var(--nx-accent)';
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss?.(), 300);
    }, 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
      borderLeft: `4px solid ${color}`, borderRadius: 10,
      padding: '12px 14px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
      maxWidth: 340, transform: visible ? 'translateX(0)' : 'translateX(110%)',
      transition: 'transform 0.3s ease',
    }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: color + '1a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <NexusIcon name={icon} size={13} style={{ color }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)', marginBottom: 2 }}>{notif?.title}</div>
        {notif?.body && (
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {notif.body}
          </div>
        )}
      </div>
      <button type="button" onClick={() => onDismiss?.()} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0 }}>
        <NexusIcon name="X" size={12} style={{ color: 'var(--nx-text-muted)' }} />
      </button>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   NOTIFICATION PREFERENCES
   ══════════════════════════════════════════════════════════════ */
const PREF_TYPES = [
  { key: 'DEAL_ASSIGNED',    label: 'Deal Assigned to Me' },
  { key: 'TASK_ASSIGNED',    label: 'Task Assigned to Me' },
  { key: 'MEETING_REMINDER', label: 'Meeting Reminders' },
  { key: 'MENTION',          label: 'Mentions (@me)' },
  { key: 'STAGE_CHANGED',    label: 'Stage Changed' },
  { key: 'FOLLOWUP_DUE',     label: 'Follow-up Due' },
  { key: 'ACTIVITY_REMINDER',label: 'Activity Reminders' },
  { key: 'COMMENT_ADDED',    label: 'Comments Added' },
  { key: 'RECORD_ASSIGNED',  label: 'Record Assigned' },
];

const PREF_KEY = 'nx-notif-prefs';

const _localPrefs = () => {
  try { return JSON.parse(localStorage.getItem(PREF_KEY) || '{}'); } catch { return {}; }
};
const _saveLocal = (prefs) => {
  try { localStorage.setItem(PREF_KEY, JSON.stringify(prefs)); } catch { /* silent */ }
};

const NotificationPreferences = () => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const [prefs,   setPrefs]   = useState(_localPrefs);
  const [saving,  setSaving]  = useState(null);
  const [apiOk,   setApiOk]   = useState(true);

  // Load from backend; fall back to localStorage on failure
  useEffect(() => {
    if (!wsId) return;
    window.__nx_api?.notificationPreferences?.getAll?.(wsId)
      .then(rows => {
        if (!Array.isArray(rows)) return;
        const map = {};
        for (const r of rows) if (r.type) map[r.type] = r.enabled !== false;
        setPrefs(prev => ({ ...prev, ...map }));
        setApiOk(true);
      })
      .catch(() => setApiOk(false));
  }, [wsId]);

  const toggle = async (key) => {
    if (saving === key) return;
    const enabled = prefs[key] === false;
    const next = { ...prefs, [key]: enabled };
    setPrefs(next);
    _saveLocal(next);
    if (!wsId || !apiOk) return;
    setSaving(key);
    try {
      await window.__nx_api?.notificationPreferences?.setPreference?.(wsId, key, enabled);
    } catch {
      setApiOk(false);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: 'var(--nx-text)' }}>Notification Preferences</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {PREF_TYPES.map(p => (
          <div key={p.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--nx-surface-alt)', borderRadius: 8, border: '1px solid var(--nx-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <NexusIcon name={NOTIF_ICON[p.key] || 'Bell'} size={14} style={{ color: NOTIF_COLOR[p.key] || 'var(--nx-accent)' }} />
              <span style={{ fontSize: 13, color: 'var(--nx-text)' }}>{p.label}</span>
            </div>
            <button
              type="button"
              onClick={() => toggle(p.key)}
              aria-pressed={prefs[p.key] !== false}
              aria-label={p.label}
              disabled={saving === p.key}
              style={{
                width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                background: prefs[p.key] !== false ? 'var(--nx-accent)' : 'var(--nx-border)',
                position: 'relative', transition: 'background 0.2s',
                opacity: saving === p.key ? 0.6 : 1,
              }}
            >
              <span style={{
                position: 'absolute', top: 3, width: 16, height: 16, borderRadius: '50%',
                background: '#fff', transition: 'left 0.2s',
                left: prefs[p.key] !== false ? 21 : 3,
              }} />
            </button>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--nx-text-muted)' }}>
        {apiOk ? 'Preferences saved to your account.' : 'Preferences saved locally in your browser.'}
      </div>
    </div>
  );
};

/* ── registrations ── */
window.NotificationItem       = NotificationItem;
window.NotificationCenter     = NotificationCenter;
window.NotificationBell       = NotificationBell;
window.NotificationToast      = NotificationToast;
window.NotificationPreferences = NotificationPreferences;
